import { Injectable } from '@angular/core';
import { Workbook, Worksheet } from 'exceljs';
import { OrgDraftPart, StaffRole, StaffRow } from '@newgenus/common';
import {
    ORG_EXCEL_SCHEMA_VERSION,
    SHEET_META,
    SHEET_STAFF,
    SHEET_STRUCTURE,
} from './org-excel.service';

/**
 * Default colour theme applied to imported parts that have no `colourBg/Fg/Link`.
 * Mirrors `step-3-structure.component.ts`'s `DEFAULT_COLOUR` so a freshly
 * imported part renders identically to a freshly created one.
 */
const DEFAULT_COLOUR = {
    background: '#1976d2',
    color: '#ffffff',
    linkColor: '#0d47a1',
} as const;

/** Allowed values for the `partType` column. */
const VALID_PART_TYPES: ReadonlySet<OrgDraftPart['partType']> = new Set([
    'executive',
    'division',
    'department',
    'section',
    'unit',
    'post',
]);

/** Allowed values for the `headerRole` column (empty string = none). */
const VALID_HEADER_ROLES: ReadonlySet<string> = new Set(['', 'head', 'deputy', 'assistant']);

/** Allowed values for the `Staff.role` column. */
const VALID_STAFF_ROLES: ReadonlySet<StaffRole> = new Set(['owner', 'manager', 'member']);

/** Loose RFC-5322 email shape — good enough for client-side gating. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Truthy values accepted in boolean-shaped cells (e.g. `isCeoPost`). Case-
 * insensitive — anything else (including empty) reads as `false`. Aligns with
 * plan addendum 19's acceptance list.
 */
const TRUTHY_CELL_VALUES: ReadonlySet<string> = new Set([
    'true', 'yes', 'y', '1', '✓', '✔', 'x',
]);

function parseBooleanCell(raw: string | number | boolean | null | undefined): boolean {
    if (raw == null || raw === '') return false;
    if (typeof raw === 'boolean') return raw;
    if (typeof raw === 'number') return raw === 1;
    return TRUTHY_CELL_VALUES.has(raw.trim().toLowerCase());
}

/**
 * Per-row issue surfaced by the importer. Errors block the import; warnings
 * surface in the success snackbar but don't prevent loading.
 */
export interface ImportIssue {
    severity: 'error' | 'warning';
    /** 1-based row number in the source sheet (header row = 1). `0` = sheet-level issue. */
    row: number;
    sheet: 'Structure' | 'Staff' | '_Meta' | 'workbook';
    message: string;
}

/** Validation-only result from {@link OrgExcelImportService.validateWorkbook}. */
export interface ImportValidationResult {
    ok: boolean;
    issues: ImportIssue[];
    /** Populated when `ok === true` — ready to feed into the wizard state. */
    data?: ImportedDraftPayload;
    /** Schema version found in `_Meta`. `undefined` if missing. */
    schemaVersion?: number;
}

/**
 * Normalised payload the importer produces — shaped to drop straight into the
 * wizard's existing state slots:
 *   - `parts` → `OrgWizardStateService.setStructure(...)`
 *   - `staff` → looped through `OrgWizardStateService.addStaffRow(...)`
 *
 * The wizard's existing Step 4 logic (debounced `checkEmail`, badges,
 * resolution status) then runs as normal — the importer does not bypass any
 * existing flow.
 */
export interface ImportedDraftPayload {
    parts: OrgDraftPart[];
    staff: Array<Partial<StaffRow>>;
    sourceOrgKey: string;
    sourceOrgName: string;
}

/**
 * Excel → OrgDraft importer. Inverse of {@link OrgExcelService}.
 *
 * Pure transform — no Firestore reads, no network. Reads from a `File`
 * (browser drop / picker) or an `ArrayBuffer` (tests, Node fixtures).
 *
 * Validation pipeline (Plan §16.6):
 *   1. Workbook present, all 3 sheets exist.
 *   2. `_Meta.schemaVersion` is in `[1, ORG_EXCEL_SCHEMA_VERSION]`.
 *   3. `Structure` header row matches the expected column names exactly.
 *   4. Per-row: `partKey` non-empty + unique; `partType` in the allowed set;
 *      `headerRole` (if set) in the allowed set; `parentPartKey` either empty
 *      or pointing to an earlier row's `partKey` (orphans → warning, imported
 *      as roots).
 *   5. No cycles in the parent chain.
 *   6. `Staff` header row matches; emails RFC-shaped; `postPartKey` (if set)
 *      points to an existing `partKey` (mismatches → warning, dropped row).
 *
 * Normalisation:
 *   - Sibling `id` rebuilt from row order within each parent (matches DFS
 *     export order, so round-trip is byte-stable on Structure + Staff).
 *   - `depth` rebuilt from parent walk.
 *   - `colour` reconstructed from `colourBg/Fg/Link`; missing values fall back
 *     to {@link DEFAULT_COLOUR} so the part renders correctly in Step 3.
 *   - `vfp` and `headerRole` round-trip verbatim.
 *   - `path` is read-only on export and is NOT round-tripped — Step 3
 *     recomputes it from the parent chain on render.
 */
@Injectable({ providedIn: 'root' })
export class OrgExcelImportService {
    /** Convenience entry point for the Step 1 file picker. */
    public async parseFile(file: File): Promise<ImportValidationResult> {
        const buffer = await file.arrayBuffer();
        return this.parseArrayBuffer(buffer);
    }

    /** Test / Node-side entry point. */
    public async parseArrayBuffer(buffer: ArrayBuffer): Promise<ImportValidationResult> {
        const wb = new Workbook();
        try {
            await wb.xlsx.load(buffer);
        } catch (err) {
            return {
                ok: false,
                issues: [{
                    severity: 'error',
                    row: 0,
                    sheet: 'workbook',
                    message: `Could not read workbook: ${(err as Error).message ?? String(err)}`,
                }],
            };
        }
        return this.validateWorkbook(wb);
    }

    public validateWorkbook(wb: Workbook): ImportValidationResult {
        const issues: ImportIssue[] = [];

        const structureWs = wb.getWorksheet(SHEET_STRUCTURE);
        const staffWs = wb.getWorksheet(SHEET_STAFF);
        const metaWs = wb.getWorksheet(SHEET_META);

        if (!structureWs) issues.push({ severity: 'error', row: 0, sheet: 'workbook', message: `Missing required sheet: ${SHEET_STRUCTURE}` });
        if (!staffWs) issues.push({ severity: 'error', row: 0, sheet: 'workbook', message: `Missing required sheet: ${SHEET_STAFF}` });
        if (!metaWs) issues.push({ severity: 'error', row: 0, sheet: 'workbook', message: `Missing required sheet: ${SHEET_META}` });

        if (!structureWs || !staffWs || !metaWs) {
            return { ok: false, issues };
        }

        // ── Meta gate ────────────────────────────────────────────────────
        const meta = this.readMeta(metaWs);
        const schemaVersion = Number(meta['schemaVersion']);
        if (!Number.isFinite(schemaVersion) || schemaVersion < 1) {
            issues.push({
                severity: 'error',
                row: 0,
                sheet: '_Meta',
                message: `Invalid schemaVersion (${meta['schemaVersion'] ?? '<missing>'}). Expected 1..${ORG_EXCEL_SCHEMA_VERSION}.`,
            });
            return { ok: false, issues };
        }
        if (schemaVersion > ORG_EXCEL_SCHEMA_VERSION) {
            issues.push({
                severity: 'error',
                row: 0,
                sheet: '_Meta',
                message: `Workbook schemaVersion ${schemaVersion} is newer than this app supports (${ORG_EXCEL_SCHEMA_VERSION}). Update Newgenus and try again.`,
            });
            return { ok: false, issues, schemaVersion };
        }

        // ── Structure ────────────────────────────────────────────────────
        // v1 required columns. `isCeoPost` was added in v2 and is read by name
        // when present, so it never blocks v1 imports.
        const structureRequired = ['partKey', 'parentPartKey', 'partType', 'name', 'headerRole', 'colourBg', 'colourFg', 'colourLink', 'vfp', 'path'];
        const structureHeaders = this.buildHeaderMap(structureWs, structureRequired, 'Structure', issues);
        if (!structureHeaders) {
            return { ok: false, issues, schemaVersion };
        }

        const parts = this.readStructureRows(structureWs, structureHeaders, issues);

        // ── Staff ────────────────────────────────────────────────────────
        const staffRequired = ['email', 'firstName', 'lastName', 'role', 'postPartKey', 'postPath'];
        const staffHeaders = this.buildHeaderMap(staffWs, staffRequired, 'Staff', issues);
        if (!staffHeaders) {
            return { ok: false, issues, schemaVersion };
        }

        const partKeySet = new Set(parts.map((p) => p.partKey));
        const staff = this.readStaffRows(staffWs, staffHeaders, partKeySet, issues);

        const hasErrors = issues.some((i) => i.severity === 'error');
        if (hasErrors) {
            return { ok: false, issues, schemaVersion };
        }

        return {
            ok: true,
            issues,
            schemaVersion,
            data: {
                parts,
                staff,
                sourceOrgKey: String(meta['sourceOrgKey'] ?? ''),
                sourceOrgName: String(meta['sourceOrgName'] ?? ''),
            },
        };
    }

    // ─────────────────────────────────────────────────────────────────────
    // Sheet readers
    // ─────────────────────────────────────────────────────────────────────

    private readMeta(ws: Worksheet): Record<string, unknown> {
        const out: Record<string, unknown> = {};
        ws.eachRow({ includeEmpty: false }, (row, n) => {
            if (n === 1) return; // header
            const key = this.cellString(row.getCell(1));
            const val = this.cellString(row.getCell(2));
            if (key) out[key] = val;
        });
        return out;
    }

    private readStructureRows(ws: Worksheet, headers: Map<string, number>, issues: ImportIssue[]): OrgDraftPart[] {
        type Raw = {
            row: number;
            partKey: string;
            parentPartKey: string;
            partType: string;
            name: string;
            headerRole: string;
            colourBg: string;
            colourFg: string;
            colourLink: string;
            vfp: string;
            isCeoPost: boolean;
        };
        const raws: Raw[] = [];
        const seenKeys = new Set<string>();
        // v2 — null = column absent in this workbook; otherwise 1-based index.
        const isCeoPostCol = headers.get('isCeoPost') ?? null;
        const ceoRowsClaimed: Array<{ row: number; partKey: string; name: string }> = [];

        const colOf = (name: string): number => {
            const idx = headers.get(name);
            if (idx == null) {
                throw new Error(`Internal error: required column "${name}" missing from header map.`);
            }
            return idx;
        };

        ws.eachRow({ includeEmpty: false }, (row, n) => {
            if (n === 1) return; // header
            const partKey = this.cellString(row.getCell(colOf('partKey')));
            if (!partKey) {
                issues.push({ severity: 'warning', sheet: 'Structure', row: n, message: 'Empty partKey — row skipped.' });
                return;
            }
            if (seenKeys.has(partKey)) {
                issues.push({ severity: 'error', sheet: 'Structure', row: n, message: `Duplicate partKey "${partKey}".` });
                return;
            }
            seenKeys.add(partKey);

            const partType = this.cellString(row.getCell(colOf('partType'))).toLowerCase();
            if (!VALID_PART_TYPES.has(partType as OrgDraftPart['partType'])) {
                issues.push({
                    severity: 'error',
                    sheet: 'Structure',
                    row: n,
                    message: `Invalid partType "${partType}" for partKey "${partKey}". Expected one of: ${[...VALID_PART_TYPES].join(', ')}.`,
                });
                return;
            }

            const headerRole = this.cellString(row.getCell(colOf('headerRole'))).toLowerCase();
            if (!VALID_HEADER_ROLES.has(headerRole)) {
                issues.push({
                    severity: 'error',
                    sheet: 'Structure',
                    row: n,
                    message: `Invalid headerRole "${headerRole}" for partKey "${partKey}". Expected blank, head, deputy, or assistant.`,
                });
                return;
            }

            const name = this.cellString(row.getCell(colOf('name')));

            let isCeoPost = false;
            if (isCeoPostCol != null) {
                const cell = row.getCell(isCeoPostCol);
                const raw = (cell?.value ?? null) as string | number | boolean | null;
                isCeoPost = parseBooleanCell(raw);
                if (isCeoPost && partType !== 'post') {
                    issues.push({
                        severity: 'error',
                        sheet: 'Structure',
                        row: n,
                        message: `isCeoPost may only be set on a post row — "${name || partKey}" has partType "${partType}".`,
                    });
                    return;
                }
                if (isCeoPost) {
                    ceoRowsClaimed.push({ row: n, partKey, name });
                }
            }

            raws.push({
                row: n,
                partKey,
                parentPartKey: this.cellString(row.getCell(colOf('parentPartKey'))),
                partType,
                name,
                headerRole,
                colourBg: this.cellString(row.getCell(colOf('colourBg'))),
                colourFg: this.cellString(row.getCell(colOf('colourFg'))),
                colourLink: this.cellString(row.getCell(colOf('colourLink'))),
                vfp: this.cellString(row.getCell(colOf('vfp'))),
                isCeoPost,
            });
        });

        if (ceoRowsClaimed.length > 1) {
            const where = ceoRowsClaimed.map((c) => `row ${c.row} (${c.name || c.partKey})`).join(', ');
            issues.push({
                severity: 'error',
                sheet: 'Structure',
                row: ceoRowsClaimed[0].row,
                message: `isCeoPost must be set on at most ONE row. Found ${ceoRowsClaimed.length}: ${where}.`,
            });
        }

        // Validate parent references and detect cycles before normalisation.
        for (const r of raws) {
            if (r.parentPartKey && !seenKeys.has(r.parentPartKey)) {
                issues.push({
                    severity: 'warning',
                    sheet: 'Structure',
                    row: r.row,
                    message: `parentPartKey "${r.parentPartKey}" not found — "${r.name || r.partKey}" imported as a root.`,
                });
                r.parentPartKey = '';
            }
        }
        this.detectCycles(raws, issues);

        // Normalise: rebuild id (sibling order = source row order), depth (parent walk),
        // colour (with default fallback). `path` is recomputed by the wizard on render
        // and is intentionally NOT round-tripped.
        const childCounter = new Map<string, number>();
        const depthOf = new Map<string, number>();
        const parentOf = new Map(raws.map((r) => [r.partKey, r.parentPartKey]));

        const computeDepth = (partKey: string, guard = new Set<string>()): number => {
            if (depthOf.has(partKey)) return depthOf.get(partKey) as number;
            if (guard.has(partKey)) return 0; // cycle — already reported
            guard.add(partKey);
            const parent = parentOf.get(partKey) ?? '';
            const d = parent ? computeDepth(parent, guard) + 1 : 0;
            depthOf.set(partKey, d);
            return d;
        };

        const parts: OrgDraftPart[] = raws.map((r) => {
            const id = childCounter.get(r.parentPartKey) ?? 0;
            childCounter.set(r.parentPartKey, id + 1);
            const depth = computeDepth(r.partKey);

            const part: OrgDraftPart = {
                partKey: r.partKey,
                name: r.name,
                partType: r.partType as OrgDraftPart['partType'],
                parentPartKey: r.parentPartKey,
                id,
                depth,
            };
            if (r.vfp) part.vfp = r.vfp;
            if (r.headerRole) part.headerRole = r.headerRole as OrgDraftPart['headerRole'];
            if (r.isCeoPost) part.isCeoPost = true;

            // Colour: only emit if at least one channel is non-empty; otherwise let
            // Step 3 fall back to its own default. This keeps round-trip symmetry —
            // an export of a default-coloured part includes the colour columns
            // (the exporter always writes them), so we always reconstruct a
            // colour object on import.
            (part as { colour?: { background: string; color: string; linkColor: string } }).colour = {
                background: r.colourBg || DEFAULT_COLOUR.background,
                color: r.colourFg || DEFAULT_COLOUR.color,
                linkColor: r.colourLink || DEFAULT_COLOUR.linkColor,
            };

            return part;
        });

        return parts;
    }

    private readStaffRows(ws: Worksheet, headers: Map<string, number>, partKeySet: Set<string>, issues: ImportIssue[]): Array<Partial<StaffRow>> {
        const out: Array<Partial<StaffRow>> = [];
        const colOf = (name: string): number => {
            const idx = headers.get(name);
            if (idx == null) {
                throw new Error(`Internal error: required column "${name}" missing from header map.`);
            }
            return idx;
        };
        ws.eachRow({ includeEmpty: false }, (row, n) => {
            if (n === 1) return; // header
            const email = this.cellString(row.getCell(colOf('email'))).trim();
            if (!email) {
                issues.push({ severity: 'warning', sheet: 'Staff', row: n, message: 'Empty email — row skipped.' });
                return;
            }
            if (!EMAIL_RE.test(email)) {
                issues.push({ severity: 'warning', sheet: 'Staff', row: n, message: `Invalid email "${email}" — row skipped.` });
                return;
            }
            const role = this.cellString(row.getCell(colOf('role'))).toLowerCase() as StaffRole;
            const finalRole: StaffRole | undefined = VALID_STAFF_ROLES.has(role) ? role : undefined;
            if (role && !finalRole) {
                issues.push({
                    severity: 'warning',
                    sheet: 'Staff',
                    row: n,
                    message: `Invalid role "${role}" for "${email}" — defaulting to member.`,
                });
            }
            const postPartKey = this.cellString(row.getCell(colOf('postPartKey')));
            let finalPostKey: string | undefined = postPartKey || undefined;
            if (postPartKey && !partKeySet.has(postPartKey)) {
                issues.push({
                    severity: 'warning',
                    sheet: 'Staff',
                    row: n,
                    message: `postPartKey "${postPartKey}" not in Structure — "${email}" imported as unassigned.`,
                });
                finalPostKey = undefined;
            }

            out.push({
                email,
                firstName: this.cellString(row.getCell(colOf('firstName'))) || undefined,
                lastName: this.cellString(row.getCell(colOf('lastName'))) || undefined,
                role: finalRole ?? 'member',
                postKey: finalPostKey,
            });
        });
        return out;
    }

    // ─────────────────────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────────────────────

    /**
     * Build a `name → 1-based column index` map from the sheet's header row.
     *
     * Replaces the v1 positional `verifyHeader` so columns can be added or
     * reordered between schema versions without breaking older workbooks.
     * Required columns must all be present; extra columns (e.g. v2's
     * `isCeoPost`) are silently catalogued so reader code can opt-in via
     * `headers.get('isCeoPost')`.
     *
     * Returns `null` (and pushes errors) if any required column is missing,
     * which causes the caller to abort the import.
     */
    private buildHeaderMap(ws: Worksheet, required: string[], sheet: 'Structure' | 'Staff', issues: ImportIssue[]): Map<string, number> | null {
        const header = ws.getRow(1);
        const map = new Map<string, number>();
        const seen = new Set<string>();
        // ExcelJS reports `actualColumnCount` after the header row commits.
        const maxCol = Math.max(required.length, header.actualCellCount, header.cellCount);
        for (let i = 1; i <= maxCol; i++) {
            const name = this.cellString(header.getCell(i)).trim();
            if (!name) continue;
            if (seen.has(name)) {
                issues.push({
                    severity: 'error',
                    sheet,
                    row: 1,
                    message: `Duplicate column header "${name}".`,
                });
                continue;
            }
            seen.add(name);
            map.set(name, i);
        }
        let ok = true;
        for (const r of required) {
            if (!map.has(r)) {
                issues.push({
                    severity: 'error',
                    sheet,
                    row: 1,
                    message: `Missing required column "${r}".`,
                });
                ok = false;
            }
        }
        return ok ? map : null;
    }

    private detectCycles(
        raws: Array<{ row: number; partKey: string; parentPartKey: string; name: string }>,
        issues: ImportIssue[],
    ): void {
        const parentOf = new Map(raws.map((r) => [r.partKey, r.parentPartKey]));
        for (const r of raws) {
            const seen = new Set<string>();
            let cur: string | undefined = r.partKey;
            while (cur) {
                if (seen.has(cur)) {
                    issues.push({
                        severity: 'error',
                        sheet: 'Structure',
                        row: r.row,
                        message: `Cycle detected in parent chain at "${r.name || r.partKey}".`,
                    });
                    break;
                }
                seen.add(cur);
                cur = parentOf.get(cur) || undefined;
                if (!cur) break;
            }
        }
    }

    private cellString(cell: { value: unknown } | undefined): string {
        const v = cell?.value;
        if (v == null) return '';
        if (typeof v === 'string') return v;
        if (typeof v === 'number' || typeof v === 'boolean') return String(v);
        // Rich text / formula / hyperlink cells.
        const obj = v as { text?: string; richText?: Array<{ text: string }>; result?: unknown };
        if (typeof obj.text === 'string') return obj.text;
        if (Array.isArray(obj.richText)) return obj.richText.map((r) => r.text).join('');
        if (obj.result != null) return String(obj.result);
        return String(v);
    }
}
